import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Search, X } from 'lucide-react';
import type { CustomerToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pill } from '~/components/pill';
import { serverMessage } from '~/lib/api/server-error';
import { customerGroupsApi, customersApi } from '~/lib/api/client';
import { useIncludeExcludeLists } from '~/hooks/use-include-exclude-lists';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	groupId: number | null;
	groupName?: string;
}

const overrideKeys = (id: number) => ['group-member-overrides', id] as const;
const label = (c: CustomerToken) => [c.fn, c.ln].filter(Boolean).join(' ').trim() || c.cnm || c.eml || `#${c.id}`;

/** Debounced customer search that emits a picked customer; hides ids already used in either list. */
function CustomerSearchAdd({ used, onPick, placeholder }: { used: Set<number>; onPick: (c: CustomerToken) => void; placeholder: string }) {
	const { t } = useTranslation();
	const [q, setQ] = useState('');
	const [results, setResults] = useState<CustomerToken[]>([]);
	const [loading, setLoading] = useState(false);
	const seq = useRef(0);

	useEffect(() => {
		const term = q.trim();
		if (!term) {
			setResults([]);
			setLoading(false);
			return;
		}
		let cancelled = false;
		const s = ++seq.current;
		setLoading(true);
		const h = setTimeout(() => {
			customersApi
				.lookup(term, 8)
				.then(r => {
					if (cancelled || s !== seq.current) return;
					setResults(r.data ?? []);
				})
				.catch(() => {
					if (!cancelled && s === seq.current) setResults([]);
				})
				.finally(() => {
					if (!cancelled && s === seq.current) setLoading(false);
				});
		}, 300);
		return () => {
			cancelled = true;
			clearTimeout(h);
		};
	}, [q]);

	const visible = results.filter(c => c.id != null && !used.has(c.id));

	return (
		<div className="relative">
			<Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
			<Input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} className="pl-9" />
			{q.trim() !== '' && (
				<div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-surface shadow-md">
					{loading ? (
						<div className="flex items-center justify-center gap-2 py-4 text-sm text-foreground-muted">
							<Loader2 size={16} className="animate-spin" />
							{t('common.search')}…
						</div>
					) : visible.length === 0 ? (
						<div className="py-4 text-center text-sm text-foreground-muted">{t('common.noResults')}</div>
					) : (
						<ul className="divide-y divide-border-subtle">
							{visible.map(c => (
								<li key={c.id ?? c.eml}>
									<button
										type="button"
										className="flex w-full flex-col px-3 py-2 text-left hover:bg-muted/50"
										onClick={() => {
											onPick(c);
											setQ('');
											setResults([]);
										}}>
										<span className="truncate text-sm font-medium text-foreground">{label(c)}</span>
										<span className="truncate text-[11px] text-foreground-muted">{c.eml}</span>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
}

/**
 * Group-axis manual membership overrides: force specific customers into or out of one group, on top of
 * its dynamic expression. Backed by DynamicExpressionEntityOverride via CustomerGroups/MemberOverrides +
 * SaveMemberOverrides. Exclusion wins, so a customer cannot be in both lists.
 */
export function GroupMemberOverridesDialog({ open, onOpenChange, groupId, groupName }: Props) {
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { include, exclude, setBoth, addInclude, addExclude, removeInclude, removeExclude } = useIncludeExcludeLists<CustomerToken>(
		c => c.id
	);

	const { data } = useQuery({
		queryKey: overrideKeys(groupId ?? 0),
		queryFn: async () => {
			const res = (await customerGroupsApi.memberOverrides(groupId as number)).data;
			const incIds = res.include_customer_ids ?? [];
			const excIds = res.exclude_customer_ids ?? [];
			const all = [...incIds, ...excIds];
			const tokens = all.length > 0 ? ((await customersApi.getByIds(all)).data ?? []) : [];
			const byId = new Map(tokens.map(c => [c.id, c]));
			const resolve = (id: number) => byId.get(id) ?? ({ id } as CustomerToken);
			return { include: incIds.map(resolve), exclude: excIds.map(resolve) };
		},
		enabled: open && groupId != null && groupId > 0
	});

	useEffect(() => {
		setBoth(data?.include ?? [], data?.exclude ?? []);
	}, [data, setBoth]);

	const save = useMutation({
		mutationFn: async () =>
			(
				await customerGroupsApi.saveMemberOverrides({
					group_id: groupId as number,
					include_customer_ids: include.map(c => c.id).filter((id): id is number => id != null),
					exclude_customer_ids: exclude.map(c => c.id).filter((id): id is number => id != null)
				})
			).data,
		onSuccess: () => {
			toast.success(t('common.save'));
			qc.invalidateQueries({ queryKey: overrideKeys(groupId ?? 0) });
			onOpenChange(false);
		},
		onError: e => toast.error(serverMessage(e))
	});

	const usedIds = new Set([...include, ...exclude].map(c => c.id).filter((id): id is number => id != null));

	const chips = (list: CustomerToken[], onRemove: (c: CustomerToken) => void) =>
		list.length === 0 ? (
			<p className="px-1 py-2 text-sm text-foreground-muted">{t('customers.groups.memberOverrides.none')}</p>
		) : (
			<div className="flex flex-wrap gap-1.5">
				{list.map(c => (
					<Pill key={c.id ?? label(c)} variant="neutral">
						<span className="truncate">{label(c)}</span>
						<button
							type="button"
							className="ml-1 text-foreground-muted hover:text-foreground"
							aria-label={t('common.remove')}
							onClick={() => onRemove(c)}>
							<X size={12} />
						</button>
					</Pill>
				))}
			</div>
		);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{t('customers.groups.memberOverrides.title')}</DialogTitle>
					<DialogDescription>{groupName || t('customers.groups.memberOverrides.subtitle')}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-5">
					<div className="flex flex-col gap-2">
						<span className="text-sm font-medium text-foreground">{t('customers.groups.memberOverrides.included')}</span>
						<CustomerSearchAdd used={usedIds} onPick={addInclude} placeholder={t('customers.groups.memberOverrides.search')} />
						{chips(include, c => removeInclude(c.id))}
					</div>
					<div className="flex flex-col gap-2">
						<span className="text-sm font-medium text-foreground">{t('customers.groups.memberOverrides.excluded')}</span>
						<CustomerSearchAdd used={usedIds} onPick={addExclude} placeholder={t('customers.groups.memberOverrides.search')} />
						{chips(exclude, c => removeExclude(c.id))}
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button onClick={() => save.mutate()} disabled={save.isPending}>
						{t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
